import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, PieChart, Pie, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend,
  CartesianGrid
} from 'recharts';
import { 
  format, subMonths, subWeeks, subYears, startOfYear, 
  parseISO, getDay
} from 'date-fns';
import api from '../services/api';

// Same grade colors used in GradeCell
const COLORS = { 
  'A+': '#4CAF50', 'A': '#4CAF50', 'A-': '#4CAF50',
  'B+': '#2196F3', 'B': '#2196F3', 'B-': '#2196F3',
  'C+': '#FFEB3B', 'C': '#FFEB3B', 'C-': '#FFEB3B',
  'D+': '#FF9800', 'D': '#FF9800', 'D-': '#FF9800',
  'F': '#F44336', '': '#eee'
};

// Day of week labels
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function AnalysisPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [chartData, setChartData] = useState([]);
  const [chartType, setChartType] = useState('bar');
  const [showValue, setShowValue] = useState(true);
  const [showMA3, setShowMA3] = useState(true);
  const [showMA7, setShowMA7] = useState(false);
  const [rawGradeData, setRawGradeData] = useState([]);
  const [dayOfWeekStats, setDayOfWeekStats] = useState(null);

  // Fetch available profiles 
  useEffect(() => {
    api.get('users/?public=true')
      .then(({ data }) => {
        setProfiles(data);
      })
      .catch(err => {
        console.error("Failed to load profiles:", err);
      });
  }, []);

  // Get date range based on selected time period
  const getDateRange = () => {
    const now = new Date();
    const today = format(now, 'yyyy-MM-dd');
    
    switch (timeRange) {
      case 'week':
        return {
          start: format(subWeeks(now, 1), 'yyyy-MM-dd'),
          end: today
        };
      case 'month':
        return {
          start: format(subMonths(now, 1), 'yyyy-MM-dd'),
          end: today
        };
      case 'year':
        return {
          start: format(startOfYear(now), 'yyyy-MM-dd'),
          end: today
        };
      case 'all':
      default:
        return { start: null, end: null };
    }
  };

  // Process day of week statistics
  const processDayOfWeekStats = (rawData) => {
    if (!rawData || rawData.length < 5) return null;
    
    const dayStats = Array(7).fill(0).map(() => ({
      count: 0,
      grades: {},
      average: 0
    }));
    
    const gradesToNumber = {
      'A+': 4.33, 'A': 4.0, 'A-': 3.67,
      'B+': 3.33, 'B': 3.0, 'B-': 2.67,
      'C+': 2.33, 'C': 2.0, 'C-': 1.67,
      'D+': 1.33, 'D': 1.0, 'D-': 0.67,
      'F': 0
    };
    
    rawData.forEach(entry => {
      const dateStr = `${entry.year}-${String(entry.month).padStart(2, '0')}-${String(entry.day).padStart(2, '0')}`;
      const date = parseISO(dateStr);
      const dayOfWeek = getDay(date);
      
      dayStats[dayOfWeek].count++;
      
      const grade = entry.grade;
      dayStats[dayOfWeek].grades[grade] = (dayStats[dayOfWeek].grades[grade] || 0) + 1;
      
      // Add grade value to calculate average later
      if (gradesToNumber[grade] !== undefined) {
        dayStats[dayOfWeek].totalPoints = (dayStats[dayOfWeek].totalPoints || 0) + gradesToNumber[grade];
        dayStats[dayOfWeek].gradedCount = (dayStats[dayOfWeek].gradedCount || 0) + 1;
      }
    });
    
    // Calculate average grade for each day
    dayStats.forEach(day => {
      if (day.gradedCount) {
        day.average = day.totalPoints / day.gradedCount;
      }
    });
    
    // Format for display
    return dayStats.map((stats, index) => ({
      day: DAY_NAMES[index],
      count: stats.count,
      average: stats.average ? stats.average.toFixed(2) : 'N/A',
      topGrade: getTopGrade(stats.grades)
    }));
  };
  
  // Get the most common grade
  const getTopGrade = (grades) => {
    let maxCount = 0;
    let topGrade = 'N/A';
    
    for (const [grade, count] of Object.entries(grades)) {
      if (count > maxCount) {
        maxCount = count;
        topGrade = grade;
      }
    }
    
    return topGrade;
  };

  // Prepare data for line chart showing grade trends
  const prepareTimelineData = (entries) => {
    if (!entries || entries.length === 0) return [];
    const gradesToNumber = {
      'A+': 4.33, 'A': 4.0, 'A-': 3.67,
      'B+': 3.33, 'B': 3.0, 'B-': 2.67,
      'C+': 2.33, 'C': 2.0, 'C-': 1.67,
      'D+': 1.33, 'D': 1.0, 'D-': 0.67,
      'F': 0
    };
    // Sort entries by date
    const sortedEntries = [...entries].sort((a, b) => {
      const dateA = new Date(a.year, a.month - 1, a.day);
      const dateB = new Date(b.year, b.month - 1, b.day);
      return dateA - dateB;
    });

    // Convert to timeline format with moving average
    return sortedEntries.map((entry, index) => {
      const dateStr = `${entry.year}-${String(entry.month).padStart(2, '0')}-${String(entry.day).padStart(2, '0')}`;
      const gradeValue = gradesToNumber[entry.grade] || 0;
      // 3-day moving average
      let movingAvg3 = null;
      if (index >= 2) {
        const prev1 = gradesToNumber[sortedEntries[index-1].grade] || 0;
        const prev2 = gradesToNumber[sortedEntries[index-2].grade] || 0;
        movingAvg3 = (prev2 + prev1 + gradeValue) / 3;
      }
      // 7-day moving average
      let movingAvg7 = null;
      if (index >= 6) {
        let sum = 0;
        for (let j = index - 6; j <= index; j++) {
          sum += gradesToNumber[sortedEntries[j].grade] || 0;
        }
        movingAvg7 = sum / 7;
      }
      return {
        date: dateStr,
        grade: entry.grade,
        value: gradeValue,
        movingAvg3,
        movingAvg7
      };
    });
  };

  // Fetch stats whenever filters change
  useEffect(() => {
    setLoading(true);
    
    const dateRange = getDateRange();
    const params = { period: 'all' };
    
    if (dateRange.start) {
      params.start = dateRange.start;
      params.end = dateRange.end;
    }
    
    if (selectedProfile !== 'all') {
      params.profile_id = selectedProfile;
    }
    
    // First get the aggregated grade counts for the charts
    api.get('analysis/', { params })
      .then(({ data }) => {
        console.log('Received analysis data:', data);
        
        // Sort grades in a logical order
        const gradeOrder = { 'A+': 0, 'A': 1, 'A-': 2, 
                            'B+': 3, 'B': 4, 'B-': 5, 
                            'C+': 6, 'C': 7, 'C-': 8, 
                            'D+': 9, 'D': 10, 'D-': 11, 
                            'F': 12 };
                            
        const sortedData = [...data].sort((a, b) => {
          return (gradeOrder[a.grade] || 99) - (gradeOrder[b.grade] || 99);
        });

        // Calculate total for percentage display
        const total = sortedData.reduce((sum, item) => sum + (typeof item.count === 'number' ? item.count : 0), 0);
        console.log('Calculated total entries:', total);

        setStats(sortedData);
        setChartData(sortedData.map(item => ({
          ...item,
          // Pre-calculate percentage for pie chart
          percentage: total > 0 ? (item.count / total) * 100 : 0
        })));
        
        // Then fetch the raw entry data for timeline and day of week stats
        // Note: This would typically be a separate API endpoint in a real app
        // For now we'll use the existing endpoints and calculate client-side
        let profileFilter = selectedProfile !== 'all' ? selectedProfile : '';
        
        // Get raw entries for processing day-of-week stats and timeline
        api.get(`users/${profileFilter || '1'}/grades/`, {
          params: {
            start: dateRange.start || '2000-01-01',
            end: dateRange.end || '2100-12-31'
          }
        })
        .then(({ data: entriesData }) => {
          setRawGradeData(entriesData);
          setDayOfWeekStats(processDayOfWeekStats(entriesData));
          setLoading(false);
        })
        .catch(err => {
          console.error("Failed to load raw entries:", err);
          setLoading(false);
        });
      })
      .catch(err => {
        console.error("Analysis error:", err);
        setError("Failed to load analysis data");
        setLoading(false);
      });
  }, [selectedProfile, timeRange]);

  // Get title based on current filters
  const getTitle = () => {
    let title = "Grade Distribution";
    if (selectedProfile !== 'all') {
      const profile = profiles.find(p => p.id.toString() === selectedProfile);
      if (profile) {
        title += ` - ${profile.display_name || profile.username}`;
      }
    } else {
      title += " - All Profiles";
    }
    
    switch (timeRange) {
      case 'week': 
        title += " (Past Week)"; 
        break;
      case 'month': 
        title += " (Past Month)"; 
        break;
      case 'year': 
        title += " (This Year)"; 
        break;
      default: 
        title += " (All Time)";
    }
    
    return title;
  };
  
  // Calculate total entries - improved calculation
  const getTotalEntries = () => {
    if (!chartData || chartData.length === 0) return 0;
    const total = chartData.reduce((sum, item) => {
      const countValue = typeof item.count === 'number' ? item.count : 0;
      return sum + countValue;
    }, 0);
    return total;
  };
  
  const timelineData = prepareTimelineData(rawGradeData);
  const totalEntries = getTotalEntries();
  console.log('Total entries calculated:', totalEntries);
  console.log('Chart data:', chartData);

  if (loading) return <p>Loading analysis…</p>;
  if (error) return <p style={{color: 'red'}}>{error}</p>;
  if (!chartData || chartData.length === 0) return <p>No grading data available for analysis.</p>;

  // Custom label formatter function that safely handles percentages
  const safePercentLabel = (entry) => {
    if (!entry || !entry.count) return `${entry.grade}: 0%`;
    
    // For debugging
    console.log('Entry in pie label:', entry);
    
    // Calculate percentage directly
    const percentage = Math.round((entry.count / totalEntries) * 100);
    return `${entry.grade}: ${percentage}%`;
  };

  // Export handlers for CSV and Excel
  const handleExportCsv = async () => {
    try {
      const dateRange = getDateRange();
      const paramsObj = {};
      if (dateRange.start) {
        paramsObj.start = dateRange.start;
        paramsObj.end = dateRange.end;
      }
      if (selectedProfile !== 'all') {
        paramsObj.profile_id = selectedProfile;
      }
      const response = await api.get('analysis/export/csv/', {
        params: paramsObj,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'grades_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('CSV export failed:', err);
      alert('CSV export failed – check console for details.');
    }
  };

  const handleExportExcel = async () => {
    try {
      const dateRange = getDateRange();
      const paramsObj = {};
      if (dateRange.start) {
        paramsObj.start = dateRange.start;
        paramsObj.end = dateRange.end;
      }
      if (selectedProfile !== 'all') {
        paramsObj.profile_id = selectedProfile;
      }
      const response = await api.get('analysis/export/excel/', {
        params: paramsObj,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'grades_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Excel export failed:', err);
      alert('Excel export failed – check console for details.');
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      {/* Filter Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <label htmlFor="profile-select">Profile: </label>
          <select 
            id="profile-select"
            value={selectedProfile}
            onChange={(e) => setSelectedProfile(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px' }}
          >
            <option value="all">All Profiles</option>
            {profiles.map(profile => (
              <option key={profile.id} value={profile.id}>
                {profile.display_name || profile.username}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label htmlFor="time-select">Time Period: </label>
          <select 
            id="time-select"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px' }}
          >
            <option value="all">All Time</option>
            <option value="year">This Year</option>
            <option value="month">Past Month</option>
            <option value="week">Past Week</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="chart-type">Chart Type: </label>
          <select 
            id="chart-type"
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
            style={{ padding: '0.5rem', borderRadius: '4px' }}
          >
            <option value="bar">Bar Chart</option>
            <option value="pie">Pie Chart</option>
            <option value="line">Timeline</option>
          </select>
        </div>
      </div>
      
      <h2>{getTitle()}</h2>
      <p>Total entries: {totalEntries}</p>
      <div style={{ marginBottom: '1rem' }}>
        <button onClick={handleExportCsv}>Export CSV</button>
        <button onClick={handleExportExcel} style={{ marginLeft: '1rem' }}>Export Excel</button>
      </div>

      {/* Show toggles for line chart series */}
      {chartType === 'line' && (
        <div style={{ marginBottom: '1rem' }}>
          <label>
            <input type="checkbox" checked={showValue} onChange={() => setShowValue(!showValue)} /> Data
          </label>
          <label style={{ marginLeft: '1rem' }}>
            <input type="checkbox" checked={showMA3} onChange={() => setShowMA3(!showMA3)} /> 3-Day MA
          </label>
          <label style={{ marginLeft: '1rem' }}>
            <input type="checkbox" checked={showMA7} onChange={() => setShowMA7(!showMA7)} /> 7-Day MA
          </label>
        </div>
      )}

      {/* Bar Chart */}
      {chartType === 'bar' && (
        <div style={{ height: '400px', marginBottom: '2rem' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
              <XAxis dataKey="grade" angle={-45} textAnchor="end" />
              <YAxis />
              <Tooltip
                formatter={(value, name, props) => {
                  const percentage = totalEntries > 0 ? ((value / totalEntries) * 100).toFixed(1) + '%' : '0%';
                  return [`${value} entries (${percentage})`, 'Count'];
                }}
                labelFormatter={(grade) => `Grade: ${grade}`}
              />
              <Legend />
              <Bar dataKey="count" name="Number of entries">
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[entry.grade] || '#ccc'} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {/* Pie Chart - with fixed label formatter */}
      {chartType === 'pie' && (
        <div style={{ height: '400px', marginBottom: '2rem' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={true}
                outerRadius={150}
                dataKey="count"
                nameKey="grade"
                label={safePercentLabel}
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[entry.grade] || '#ccc'} 
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => {
                  // Calculate percentage directly from the value
                  const percentage = totalEntries > 0 ? ((value / totalEntries) * 100).toFixed(1) + '%' : '0%';
                  return [`${value} entries (${percentage})`, name];
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {/* Timeline / Line Chart */}
      {chartType === 'line' && timelineData.length > 0 && (
        <div style={{ height: '400px', marginBottom: '2rem' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={timelineData}
              margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                angle={-45} 
                textAnchor="end"
                tickFormatter={value => value.slice(5)} // Show just MM-DD
              />
              <YAxis 
                domain={[0, 5]} 
                ticks={[0, 1, 2, 3, 4]} 
                tickFormatter={value => {
                  switch(value) {
                    case 4: return 'A';
                    case 3: return 'B';
                    case 2: return 'C';
                    case 1: return 'D';
                    case 0: return 'F';
                    default: return '';
                  }
                }}
              />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'value') {
                    // Convert numeric value back to letter grade
                    if (value >= 4.3) return 'A+'; 
                    if (value >= 4.0) return 'A';
                    if (value >= 3.7) return 'A-';
                    if (value >= 3.3) return 'B+';
                    if (value >= 3.0) return 'B';
                    if (value >= 2.7) return 'B-';
                    if (value >= 2.3) return 'C+';
                    if (value >= 2.0) return 'C';
                    if (value >= 1.7) return 'C-';
                    if (value >= 1.3) return 'D+';
                    if (value >= 1.0) return 'D';
                    if (value >= 0.7) return 'D-';
                    if (value >= 0) return 'F';
                    return 'N/A';
                  }
                  if (name === 'movingAvg3' || name === 'movingAvg7') {
                    return value ? value.toFixed(2) : 'N/A';
                  }
                  return value;
                }}
                labelFormatter={value => `Date: ${value}`}
              />
              <Legend />
              {showValue && (
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  name="Grade" 
                  stroke="#8884d8" 
                  activeDot={{ r: 8 }}
                  dot={{ r: 4 }}
                />
              )}
              {showMA3 && timelineData.some(d => d.movingAvg3) && (
                <Line 
                  type="monotone" 
                  dataKey="movingAvg3" 
                  name="3-Day Moving Avg" 
                  stroke="#82ca9d" 
                  strokeWidth={2} 
                  dot={false}
                />
              )}
              {showMA7 && timelineData.some(d => d.movingAvg7) && (
                <Line 
                  type="monotone" 
                  dataKey="movingAvg7" 
                  name="7-Day Moving Avg" 
                  stroke="#ffc658" 
                  strokeWidth={2} 
                  dot={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {/* Stats Table */}
      <div style={{ maxWidth: '500px', margin: '0 auto', marginBottom: '2rem' }}>
        <h3>Grade Summary</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Grade</th>
              <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Count</th>
              <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map(stat => {
              const percentage = totalEntries > 0 
                ? ((stat.count / totalEntries) * 100).toFixed(1) 
                : '0';
              
              return (
                <tr key={stat.grade}>
                  <td style={{ 
                    padding: '8px', 
                    textAlign: 'left',
                    borderBottom: '1px solid #eee'
                  }}>
                    <span style={{
                      display: 'inline-block',
                      width: '20px',
                      height: '20px',
                      backgroundColor: COLORS[stat.grade] || '#ccc',
                      marginRight: '8px',
                      borderRadius: '3px',
                      verticalAlign: 'middle'
                    }}></span>
                    {stat.grade}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                    {stat.count}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                    {percentage}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Day of Week Stats - Only if we have enough data points */}
      {dayOfWeekStats && dayOfWeekStats.filter(d => d.count > 0).length > 0 && (
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h3>Day of Week Analysis</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Day</th>
                <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Number of Entries</th>
                <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Average Grade</th>
                <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Most Common Grade</th>
              </tr>
            </thead>
            <tbody>
              {dayOfWeekStats.map(day => (
                <tr key={day.day}>
                  <td style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #eee' }}>
                    {day.day}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                    {day.count}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>
                    {day.average}
                  </td>
                  <td style={{ 
                    padding: '8px', 
                    textAlign: 'right', 
                    borderBottom: '1px solid #eee',
                    color: day.topGrade !== 'N/A' ? COLORS[day.topGrade.charAt(0)] || 'black' : 'black',
                    fontWeight: 'bold'
                  }}>
                    {day.topGrade}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}